import {PlayerReplay, ReplayEntityV2} from "@repo/shared";

import {PointEntity, ShardCollected} from "../../types";
import {decodeRLEToActions} from "../../utils/utils";
import {Player} from "../player/Player.js";
import {GhostPlayerController} from "../player/controller/GhostPlayerController.js";
import {SimulationManager} from "./SimulationManager.js";
import {World} from "./World.js";

// Assuming types file path

/**
 * Represents the detailed result of a single player's verification.
 */
export interface PlayerVerificationDetails {
  id: string;
  ownerId: string;
  instanceId: number;
  isValid: boolean;
  positionMatch: boolean;
  velocityMatch: boolean;
  shardsMatch: boolean;
  diedMatch: boolean;
  expectedState: PlayerReplay["finalState"];
  actualState: PlayerReplay["finalState"];
}

/**
 * The overall result of verifying an entire ReplayEntityV2.
 */
export interface ReplayVerificationResult {
  isValid: boolean; // True only if all top-level and player-level checks pass
  completedMatch: boolean;
  totalStepsMatch: boolean;
  playerResults: Record<string, PlayerVerificationDetails>; // Results per player
  verificationTimeMs: number;
}

/**
 * A stateless utility class for verifying the integrity of a V2 replay.
 * It runs a full, deterministic simulation based on the replay's action data
 * and compares the final state to the expected outcome.
 */
export class ReplayVerifier {
  private readonly world: World;

  constructor(world: World) {
    this.world = world;
  }

  /**
   * Verifies an entire ReplayEntityV2.
   */
  public verify(replay: ReplayEntityV2): ReplayVerificationResult {
    const startTime = performance.now();

    // --- 1. Setup the Simulation ---
    const playerReplays = Object.values(replay.playerReplays);
    const players = playerReplays.map(
      (pr) => new Player(pr.ownerId, pr.instanceId),
    );
    const controllers = playerReplays.map(
      (pr) => new GhostPlayerController(pr.id, decodeRLEToActions(pr.actions)),
    );

    const simulation = new SimulationManager(this.world, players);

    // --- 2. Run the Simulation ---
    for (let step = 0; step < Math.ceil(replay.totalSteps); step++) {
      const actionsForStep: Record<string, boolean> = {};
      for (const controller of controllers) {
        actionsForStep[controller.uid] = controller.getActionForStep(step);
      }
      // We don't need to process the events, just run the simulation.
      simulation.runStep(actionsForStep);
    }

    // --- 3. Compare Final States ---
    const playerResults: Record<string, PlayerVerificationDetails> = {};
    let allPlayersAreValid = true;

    for (const playerReplay of playerReplays) {
      const id = playerReplay.id;
      const ownerId = playerReplay.ownerId;
      const instanceId = playerReplay.instanceId;
      const actualPlayer = simulation.players.get(id);
      const expectedState = playerReplay.finalState;

      if (!actualPlayer) {
        // This should never happen if setup is correct.
        allPlayersAreValid = false;
        continue;
      }

      const actualState: PlayerReplay["finalState"] = {
        position: actualPlayer.currentPosition.clone(),
        velocity: actualPlayer.velocity.clone(),
        died: actualPlayer.isDead,
        collectedShards: actualPlayer.collectedShardsWithSteps,
      };

      const positionMatch = this.comparePoints(
        actualState.position,
        expectedState.position,
      );
      const velocityMatch = this.comparePoints(
        actualState.velocity,
        expectedState.velocity,
      );
      const shardsMatch = this.compareShards(
        actualState.collectedShards,
        expectedState.collectedShards,
      );
      const diedMatch = actualState.died === expectedState.died;

      const isPlayerValid =
        positionMatch && velocityMatch && shardsMatch && diedMatch;
      if (!isPlayerValid) {
        allPlayersAreValid = false;
      }

      playerResults[id] = {
        id,
        ownerId,
        instanceId,
        isValid: isPlayerValid,
        positionMatch,
        velocityMatch,
        shardsMatch,
        diedMatch,
        expectedState,
        actualState,
      };
    }

    // --- 4. Compare Top-Level Outcomes ---
    // The replay is considered completed if ANY player is marked as completed.
    const completedMatch = simulation.isWon === replay.completed;

    // totalSteps should match exactly.
    const actualSteps = simulation.isWon
      ? simulation.getLastShardCollectedStep()
      : simulation.currentStep;
    const totalStepsMatch = actualSteps === replay.totalSteps;
    if (!totalStepsMatch)
      console.warn(
        "ActualSteps:",
        actualSteps,
        " ReplaySteps:",
        replay.totalSteps,
      );

    // --- 5. Determine Overall Validity and Return Result ---
    const overallIsValid =
      allPlayersAreValid && completedMatch && totalStepsMatch;
    const verificationTimeMs = performance.now() - startTime;

    return {
      isValid: overallIsValid,
      completedMatch,
      totalStepsMatch,
      playerResults,
      verificationTimeMs,
    };
  }

  // --- Private Helper Methods for Comparison ---

  private comparePoints(
    p1: PointEntity,
    p2: PointEntity,
    tolerance = 1e-6,
  ): boolean {
    return (
      Math.abs(p1.x - p2.x) < tolerance && Math.abs(p1.y - p2.y) < tolerance
    );
  }

  private compareShards(s1: ShardCollected[], s2: ShardCollected[]): boolean {
    if (s1.length !== s2.length) return false;
    // Create a string representation for easy comparison, insensitive to order.
    const set1 = new Set(s1.map((s) => `${s.shardId}:${s.steps.toFixed(6)}`));
    const set2 = new Set(s2.map((s) => `${s.shardId}:${s.steps.toFixed(6)}`));
    if (set1.size !== set2.size) return false;
    for (const item of set1) {
      if (!set2.has(item)) return false;
    }
    return true;
  }
}
